import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import { IInvestmentDataModel } from '../../../data-models/investment.data-model';
import { COMPANY_VIEW_TYPE } from '../../../data-models/view-config.data-model';
import { ViewModel } from '../../../models/view.model';
import { TemporalPlainDateFromJsDate } from '../../../util/date-utilts';
import { useSelectedCurrency } from '../../../hooks/useSelectedCurrency';
import { metricsByDateAtom, metricsFilteredByViewAtom } from '../state/MetricsStateJ';
import { selectedViewDatePFJ } from '../state/PageStateJ';
import { previousQuarterMetricsFilteredByViewAtom } from '../state/PreviousQuarterMetricsStateJ';
import {
  roundTrackerMetricsByDateAtom,
  roundTrackerMetricsFilteredByViewAtom,
} from '../state/RoundTrackerMetricsStateJ';
import { useInitializeMetricsData } from './useInitializeMetricsData';

export function useCurrentViewMetrics(view: ViewModel) {
  const date = useAtomValue(selectedViewDatePFJ);
  const { selectedCurrency } = useSelectedCurrency();
  const tDate = TemporalPlainDateFromJsDate(date);
  const { previousQuarterDate, ready, loaded } = useInitializeMetricsData();
  const tPreviousQuarterDate = TemporalPlainDateFromJsDate(previousQuarterDate);
  const metrics = useAtomValue(metricsByDateAtom({ date: tDate, currency: selectedCurrency }));
  // Suspends until the previous quarter has loaded, so the tiles never commit a missing delta.
  useAtomValue(metricsByDateAtom({ date: tPreviousQuarterDate, currency: selectedCurrency }));
  const filteredMetrics = useAtomValue(metricsFilteredByViewAtom(view.id));
  const prevMetricsFiltered = useAtomValue(previousQuarterMetricsFilteredByViewAtom(view.id));

  const { data: roundTrackerMetrics } = useAtomValue(
    roundTrackerMetricsByDateAtom({ date: tDate, currency: selectedCurrency })
  );
  const filteredRoundTrackerMetrics = useAtomValue(roundTrackerMetricsFilteredByViewAtom(view.id));

  const prevRoundTrackerMetricsFiltered = useMemo(() => {
    if (filteredRoundTrackerMetrics) {
      const previousQuarterRoundTrackerMetrics =
        filteredRoundTrackerMetrics.filter(
          (metric) => new Date(metric.investmentDate).getTime() < previousQuarterDate.getTime()
        ) ?? [];
      return filterPreviousQuarterRoundTrackerMetrics(
        filteredRoundTrackerMetrics,
        previousQuarterRoundTrackerMetrics
      );
    }
    return [];
  }, [filteredRoundTrackerMetrics, previousQuarterDate]);

  return useMemo(() => {
    return view.companyViewType === COMPANY_VIEW_TYPE.ROUND_TRACKER
      ? {
          metrics: roundTrackerMetrics,
          previousQuarterMetrics: prevRoundTrackerMetricsFiltered,
          filteredMetrics: filteredRoundTrackerMetrics,
          ready,
          loaded,
        }
      : {
          metrics,
          previousQuarterMetrics: prevMetricsFiltered,
          filteredMetrics,
          ready,
          loaded,
        };
  }, [
    filteredMetrics,
    filteredRoundTrackerMetrics,
    metrics,
    prevMetricsFiltered,
    prevRoundTrackerMetricsFiltered,
    roundTrackerMetrics,
    view.companyViewType,
    ready,
    loaded,
  ]);
}

function filterPreviousQuarterRoundTrackerMetrics(
  filteredMetrics: IInvestmentDataModel[],
  previousQuarterMetrics: IInvestmentDataModel[]
) {
  const includedCompanies = filteredMetrics.reduce((res, m) => res.add(m.companyId), new Set<number>());

  return previousQuarterMetrics.filter((m) => includedCompanies.has(m.companyId));
}
